//import { DEMO_POSTS } from "data/posts";
//import { PostDataType } from "data/types";
import React, { useEffect, useRef, useState } from "react";
import hljs from 'highlight.js';
import 'highlight.js/styles/monokai-sublime.css';
import DOMPurify from 'dompurify';
import { useNavigate, useParams } from "react-router-dom";
import Avatar from "shared/Avatar/Avatar";
import Badge from "shared/Badge/Badge";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import ButtonSecondary from "shared/Button/ButtonSecondary";
import BlogComment, { Comment } from "./BlogComment";
import NcImage from "shared/NcImage/NcImage";
import DeleteModal from "containers/BlogPage/BlogModal/DeleteModal";
import FailModal from "containers/BlogPage/BlogModal/FailModal";
import { useAuth } from "components/AuthLogin/AuthContext";
import { BlogCategory } from "data/types";
import { ArrowUp, Heart } from "@phosphor-icons/react";
import { animalAvatarUrl, randomNickname } from "utils/animalAvatar";

const josa = (name: string) => {
  const code = name.charCodeAt(name.length - 1);
  if (code >= 0xAC00 && code <= 0xD7A3) {
    return (code - 0xAC00) % 28 === 0 ? '로' : '으로';
  }
  return '로';
};

const TEXTAREA_CLASS = "flex-1 py-2.5 text-sm bg-transparent !border-0 !shadow-none !ring-0 focus:!border-0 focus:!shadow-none focus:!ring-0 focus:outline-none resize-none overflow-hidden leading-5";

const BlogSingle = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isAuthenticated, isAdmin, user, getCookie, fetchWithAuth } = useAuth();
  const currentUserId = getCookie('userId');
  const [post, setPost] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleteFailModalOpen, setIsDeleteFailModalOpen] = useState(false);
  const [comments, setComments] = useState<Comment[]>([]);
  const [newComment, setNewComment] = useState("");
  const [replyComment, setReplyComment] = useState("");
  const [replyToId, setReplyToId] = useState<number | null>(null);
  const [isSubmittingComment, setIsSubmittingComment] = useState(false);
  const commentTextareaRef = useRef<HTMLTextAreaElement>(null);
  const [commentError, setCommentError] = useState(false);
  const [commentMultiline, setCommentMultiline] = useState(false);
  const [replyMultiline, setReplyMultiline] = useState(false);
  const [liked, setLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(0);
  const [anonNickname, setAnonNickname] = useState(() => randomNickname());

  useEffect(() => {
    const fetchPost = async () => {
      try {
        const apiToken = getCookie('apiToken');
        const response = await fetch(`${process.env.REACT_APP_API_URL}/posts/${id}`, {
          headers: apiToken ? { Authorization: `Bearer ${apiToken}` } : {},
        });
        if (!response.ok) throw new Error('Failed to fetch post');
        const data = await response.json();
        setPost(data);
        const serverCount = data.like?.count ?? 0;
        const serverLiked = data.like?.isLiked ?? false;
        const localLiked = !serverLiked && localStorage.getItem(`liked_post_${id}`) === 'true';
        setLiked(serverLiked || localLiked);
        setLikeCount(serverCount);

        const viewedKey = `viewed_${id}`;
        if (!sessionStorage.getItem(viewedKey)) {
          await fetch(`${process.env.REACT_APP_API_URL}/posts/count`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ post: { ...data, type: 'view' } })
          });
          sessionStorage.setItem(viewedKey, '1');
        }

        const commentsRes = await fetch(`${process.env.REACT_APP_API_URL}/posts/${id}/comments`);
        if (commentsRes.ok) {
          setComments(await commentsRes.json());
        }
      } catch (err) {
        navigate('/blog');
      } finally {
        setLoading(false);
      }
    };

    fetchPost();
  }, [id]);

  useEffect(() => {
    if (post) {
      document.title = `${post.title} | Techfeed`;
    }
    return () => { document.title = 'Techfeed'; };
  }, [post]);

  useEffect(() => {
    if (post) {
      document.querySelectorAll('#single-entry-content pre').forEach((block) => {
        if (block instanceof HTMLElement && !block.dataset.highlighted) {
          hljs.highlightElement(block);
        }
      });
    }
  }, [post]);

  if (loading) return (
      <div className="nc-PageSingle pt-8 lg:pt-16 animate-pulse">
        <div className="container max-w-screen-md mx-auto space-y-5 px-4">
          <div className="h-6 bg-neutral-200 dark:bg-neutral-700 rounded w-24" />
          <div className="h-10 bg-neutral-200 dark:bg-neutral-700 rounded w-3/4" />
          <div className="h-5 bg-neutral-200 dark:bg-neutral-700 rounded w-full" />
          <div className="h-5 bg-neutral-200 dark:bg-neutral-700 rounded w-5/6" />
          <div className="flex items-center space-x-3 pt-2">
            <div className="w-10 h-10 bg-neutral-200 dark:bg-neutral-700 rounded-full" />
            <div className="space-y-2">
              <div className="h-4 bg-neutral-200 dark:bg-neutral-700 rounded w-32" />
              <div className="h-3 bg-neutral-200 dark:bg-neutral-700 rounded w-24" />
            </div>
          </div>
        </div>
        <div className="container my-10 sm:my-12 max-w-4xl mx-auto">
          <div className="w-full h-64 sm:h-96 bg-neutral-200 dark:bg-neutral-700 rounded-xl" />
        </div>
        <div className="container max-w-screen-md mx-auto space-y-4 px-4">
          <div className="h-4 bg-neutral-200 dark:bg-neutral-700 rounded w-full" />
          <div className="h-4 bg-neutral-200 dark:bg-neutral-700 rounded w-full" />
          <div className="h-4 bg-neutral-200 dark:bg-neutral-700 rounded w-4/5" />
          <div className="h-4 bg-neutral-200 dark:bg-neutral-700 rounded w-full" />
          <div className="h-4 bg-neutral-200 dark:bg-neutral-700 rounded w-3/4" />
        </div>
      </div>
  );
  if (!post) return null;

  const handleModify = () => {
    navigate('/blog-modify', { state: { post } });
  };

  const handleDelete = async () => {
    try {
      const response = await fetchWithAuth(`${process.env.REACT_APP_API_URL}/posts/${post.id}`, {
        method: "DELETE",
      });

      if (!response || !response.ok) {
        const errorData = response ? await response.json() : {};
        throw new Error(errorData.message || "Delete operation failed");
      }

      navigate('/blog');
    } catch (error) {
      console.error("Error:", error);
      setIsDeleteFailModalOpen(true);
    }
  };

  const submitComment = async (text: string, parentId: number | null) => {
    if (isSubmittingComment || !text.trim()) return;
    const apiToken = getCookie('apiToken');
    setIsSubmittingComment(true);
    try {
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (apiToken) headers['Authorization'] = `Bearer ${apiToken}`;

      const response = await fetch(`${process.env.REACT_APP_API_URL}/posts/${post.id}/comments`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ comment: text, content_id: post.id, parent_id: parentId, ...(!isAuthenticated && { nickname: anonNickname }) }),
      });

      if (!response.ok) throw new Error('Failed to submit comment');

      const data: Comment = await response.json();
      setComments(prev => [...prev, data]);
      setCommentError(false);
      return true;
    } catch (error) {
      console.error("Error submitting comment:", error);
      setCommentError(true);
      return false;
    } finally {
      setIsSubmittingComment(false);
    }
  };

  const handleCommentSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const ok = await submitComment(newComment, null);
    if (ok) {
      setNewComment("");
      if (commentTextareaRef.current) {
        commentTextareaRef.current.style.height = "auto";
      }
    }
  };

  const handleReplySubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const ok = await submitComment(replyComment, replyToId);
    if (ok) {
      setReplyComment("");
      setReplyToId(null);
    }
  };

  const handleCommentDelete = async (commentId: number) => {
    try {
      const response = await fetchWithAuth(`${process.env.REACT_APP_API_URL}/posts/${post.id}/comments/${commentId}`, {
        method: 'DELETE',
      });

      if (!response || !response.ok) {
        throw new Error('Failed to delete comment');
      }

      const getAllDescendantIds = (id: number, all: Comment[]): number[] => {
        const children = all.filter(c => c.parent_id === id);
        return [...children.map(c => c.id), ...children.flatMap(c => getAllDescendantIds(c.id, all))];
      };
      setComments(prev => {
        const toRemove = new Set([commentId, ...getAllDescendantIds(commentId, prev)]);
        return prev.filter(c => !toRemove.has(c.id));
      });
    } catch (error) {
      console.error("Error deleting comment:", error);
    }
  };

  const renderHeader = () => {
    return (
        <header className="container rounded-xl">
          <div className="max-w-screen-md mx-auto space-y-5">
            {post.categories?.[0] && (
                <Badge color={post.categories[0].color} name={post.categories[0].category} />
            )}
            <h1
                className=" text-neutral-900 font-medium text-3xl md:text-4xl md:!leading-[120%] lg:text-4xl dark:text-neutral-100 max-w-4xl "
                title={post.title}
            >
              {post.title}
            </h1>
            <span className="block text-base text-neutral-500 md:text-lg dark:text-neutral-400 pb-1">
            {post.desc}
          </span>

            <div className="w-full border-b border-neutral-100 dark:border-neutral-800"></div>
            <div className="flex flex-col items-baseline sm:flex-row sm:justify-between">
              <div className="nc-PostMeta2 flex items-center flex-wrap text-neutral-700 text-left dark:text-neutral-200 text-sm leading-none flex-shrink-0">
                <Avatar
                    containerClassName="flex-shrink-0"
                    sizeClass="w-8 h-8 sm:h-11 sm:w-11"
                    imgUrl={post.author.avatar}
                />
                <div className="ml-3">
                  <div className="flex items-center">
                  <span className="block font-medium">
                    {post.author.displayName}
                  </span>
                  </div>
                  <div className="text-xs mt-[6px]">
                  <span className="text-neutral-700 dark:text-neutral-300">
                    {post.date}
                  </span>
                  </div>
                </div>
              </div>
              <div className="flex items-center justify-end mt-3 sm:mt-0 sm:ml-3 w-full">
                {isAuthenticated && (String(currentUserId) === String(post.author.userId) || isAdmin) && (
                    <>
                      <ButtonSecondary
                          sizeClass="px-4 py-2 sm:px-5"
                          onClick={handleModify}
                      >
                        수정
                      </ButtonSecondary>
                      <ButtonPrimary
                          className="bg-red-500 hover:bg-red-700 ml-2"
                          sizeClass="px-4 py-2 sm:px-5"
                          onClick={() => setIsDeleteModalOpen(true)}
                      >
                        삭제
                      </ButtonPrimary>
                    </>
                )}
                <DeleteModal
                    isOpen={isDeleteModalOpen}
                    onClose={() => setIsDeleteModalOpen(false)}
                    onConfirm={async () => {
                      await handleDelete();
                      setIsDeleteModalOpen(false);
                    }}
                    mainMessage="이 글을 삭제할까요?"
                    subMessage="되돌릴 수 없어요."
                />
                <FailModal
                    isOpen={isDeleteFailModalOpen}
                    onClose={() => setIsDeleteFailModalOpen(false)}
                    title="삭제 실패"
                    mainMessage="글 삭제에 실패했어요."
                    subMessage="다시 시도해 주세요."
                />
              </div>
            </div>
          </div>
        </header>
    );
  };

  const handleLike = async () => {
    if (isAuthenticated) {
      try {
        const res = await fetchWithAuth(`${process.env.REACT_APP_API_URL}/posts/${post.id}/like`, {
          method: 'POST',
        });
        if (!res || !res.ok) return;
        const data = await res.json();
        setLiked(data.isLiked);
        setLikeCount(data.count);
      } catch (err) {
        console.error('Failed to toggle like:', err);
      }
    } else {
      const lsKey = `liked_post_${post.id}`;
      const nowLiked = !liked;
      try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/posts/${post.id}/like`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: nowLiked ? 'like' : 'unlike' }),
        });
        if (!res.ok) return;
        const data = await res.json();
        setLiked(nowLiked);
        setLikeCount(data.count);
        if (nowLiked) {
          localStorage.setItem(lsKey, 'true');
        } else {
          localStorage.removeItem(lsKey);
        }
      } catch (err) {
        console.error('Failed to toggle like:', err);
      }
    }
  };

  const renderContent = () => {
    return (
        <div
            id="single-entry-content"
            className="prose dark:prose-invert prose-sm !max-w-screen-md sm:prose lg:prose-lg mx-auto dark:prose-dark"
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(post.content, { ADD_TAGS: ['iframe'], ADD_ATTR: ['allow', 'allowfullscreen', 'frameborder'] }) }}
        />
    );
  };

  const renderTags = () => {
    return (
        <div className="max-w-screen-md mx-auto flex flex-wrap gap-2">
          {post.categories.map((category: BlogCategory, index: number) => (
              <Badge key={index} name={category.category} color={category.color} />
          ))}
        </div>
    );
  };

  const renderAuthor = () => {
    return (
        <div className="max-w-screen-md mx-auto">
          <div className="nc-SingleAuthor flex">
            <Avatar
                sizeClass="w-11 h-11 md:w-24 md:h-24"
                imgUrl={post.author.avatar}
            />
            <div className="flex flex-col ml-3 max-w-lg sm:ml-5 space-y-1">
            <span className="text-xs text-neutral-400 tracking-wider">
              작성자
            </span>
              <h2 className="text-lg font-medium text-neutral-900 dark:text-neutral-200">
                {post.author.displayName}
              </h2>
              <span className="text-sm text-neutral-500 sm:text-base dark:text-neutral-300">
              {post.author.desc ? post.author.desc : "아직 소개글이 없어요."}
            </span>
            </div>
          </div>
        </div>
    );
  };

  const renderCommentForm = () => {
    return (
        <div className="max-w-screen-md mx-auto pt-5">
          <h3 className="text-xl font-medium text-neutral-800 dark:text-neutral-200">
            댓글 ({comments.length})
          </h3>
          <form className="nc-SingleCommentForm mt-5" onSubmit={handleCommentSubmit}>
            {isAuthenticated ? (
                <span className="block mb-3 text-xs text-neutral-400 dark:text-neutral-500">
                  {(() => { const n = user?.displayName || user?.name || ''; return `${n}${josa(n)} 작성 중`; })()}
                </span>
            ) : (
                <div className="flex items-center justify-between px-1 py-2 rounded-xl">
                  <div className="flex items-center gap-2">
                    <img
                        src={animalAvatarUrl(anonNickname)}
                        alt={anonNickname}
                        className="w-9 h-9 rounded-full bg-neutral-100 dark:bg-neutral-800 flex-shrink-0"
                    />
                    <input
                        type="text"
                        value={anonNickname}
                        onChange={(e) => setAnonNickname(e.target.value)}
                        maxLength={20}
                        className="text-sm bg-transparent focus:outline-none text-neutral-700 dark:text-neutral-300 font-medium w-32"
                    />
                  </div>
                  <button
                      type="button"
                      onClick={() => setAnonNickname(randomNickname())}
                      className="h-8 px-4 rounded-xl bg-[#f2f4f6] hover:bg-[#e9ecef] text-sm font-semibold text-neutral-700 dark:bg-neutral-800 dark:hover:bg-neutral-700 dark:text-neutral-200 transition-colors flex-shrink-0"
                  >
                    랜덤 변경
                  </button>
                </div>
            )}
            <div className={`flex ${commentMultiline ? 'items-end' : 'items-center'} gap-2 rounded-xl border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 pl-4 pr-2 focus-within:ring focus-within:ring-primary-200 focus-within:ring-opacity-50 focus-within:border-primary-300 dark:focus-within:ring-primary-6000 dark:focus-within:ring-opacity-25`}>
              <textarea
                  ref={commentTextareaRef}
                  value={newComment}
                  onChange={(e) => {
                    setNewComment(e.target.value);
                    e.target.style.height = "auto";
                    e.target.style.height = e.target.scrollHeight + "px";
                    setCommentMultiline(e.target.value.includes('\n') || e.target.scrollHeight > 40);
                  }}
                  placeholder="댓글을 남겨보세요..."
                  rows={1}
                  className={`${TEXTAREA_CLASS} rounded-none`}
              />
              <button
                  type="submit"
                  disabled={isSubmittingComment || !newComment.trim()}
                  className={`flex-shrink-0 ${commentMultiline ? 'mb-2' : ''} w-7 h-7 flex items-center justify-center rounded-full bg-neutral-900 dark:bg-neutral-100 text-white dark:text-neutral-900 disabled:opacity-30 disabled:cursor-not-allowed transition-opacity`}
              >
                <ArrowUp size={14} weight="bold" />
              </button>
            </div>
            {commentError && (
                <p className="mt-1 text-xs text-red-500">댓글 등록에 실패했어요. 다시 시도해 주세요.</p>
            )}
          </form>
        </div>
    );
  };

  const renderInlineReplyForm = (parentId: number) => (
      <form className="mt-3 pl-11" onSubmit={handleReplySubmit}>
        <div className={`flex ${replyMultiline ? 'items-end' : 'items-center'} gap-2 rounded-xl border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 pl-4 pr-2 focus-within:ring focus-within:ring-primary-200 focus-within:ring-opacity-50 focus-within:border-primary-300 dark:focus-within:ring-primary-6000 dark:focus-within:ring-opacity-25`}>
          <textarea
              value={replyComment}
              onChange={(e) => {
                setReplyComment(e.target.value);
                e.target.style.height = "auto";
                e.target.style.height = e.target.scrollHeight + "px";
                setReplyMultiline(e.target.value.includes('\n') || e.target.scrollHeight > 40);
              }}
              placeholder="답글을 남겨보세요..."
              rows={1}
              className={`${TEXTAREA_CLASS} rounded-none`}
          />
          <button
              type="submit"
              disabled={isSubmittingComment || !replyComment.trim()}
              className={`flex-shrink-0 ${replyMultiline ? 'mb-2' : ''} w-7 h-7 flex items-center justify-center rounded-full bg-neutral-900 dark:bg-neutral-100 text-white dark:text-neutral-900 disabled:opacity-30 disabled:cursor-not-allowed transition-opacity`}
          >
            <ArrowUp size={14} weight="bold" />
          </button>
        </div>
        {commentError && (
            <p className="mt-1 text-xs text-red-500">등록에 실패했어요.</p>
        )}
        <button
            type="button"
            onClick={() => setReplyToId(null)}
            className="mt-1.5 text-xs text-neutral-400 hover:text-neutral-600 dark:text-neutral-500 dark:hover:text-neutral-300 transition-colors"
        >
          취소
        </button>
      </form>
  );

  const renderComment = (comment: Comment) => {
    const replies = comments.filter(reply => reply.parent_id === comment.id);
    const isReplying = replyToId === comment.id;
    return (
        <li key={comment.id}>
          <BlogComment
              comment={comment}
              isPostAuthor={String(comment.user?.userId) === String(post.author.userId)}
              onDelete={() => handleCommentDelete(comment.id)}
              onReply={(parentId) => setReplyToId(parentId)}
          />
          {isReplying && renderInlineReplyForm(comment.id)}
          {replies.length > 0 && (
              <ul className="pl-11 mt-5 space-y-5">
                {replies.map(reply => renderComment(reply))}
              </ul>
          )}
        </li>
    );
  };

  const renderCommentLists = () => {
    const topLevelComments = comments.filter(comment => comment.parent_id === null);
    return (
        <div className="max-w-screen-md mx-auto">
          {topLevelComments.length === 0 ? (
              <p className="text-neutral-400 dark:text-neutral-500 text-sm">
                아직 댓글이 없어요. 첫 번째 댓글을 남겨보세요!
              </p>
          ) : (
              <ul className="nc-SingleCommentLists space-y-5">
                {topLevelComments.map(comment => renderComment(comment))}
              </ul>
          )}
        </div>
    );
  };

  return (
      <div className="nc-PageSingle pt-8 lg:pt-16">
        {renderHeader()}
        <NcImage
            className="w-full rounded-xl"
            containerClassName="container my-10 sm:my-12 max-w-4xl mx-auto"
            src={post.featuredImage}
        />
        <div className="nc-SingleContent container space-y-10 pb-16 lg:pb-28">
          {renderContent()}
          {renderTags()}
          <div className="max-w-screen-md mx-auto flex items-center gap-3">
            <button
                onClick={handleLike}
                className={`flex items-center gap-2 text-base font-medium transition-colors cursor-pointer ${
                    liked ? 'text-red-500' : 'text-neutral-400 dark:text-neutral-500 hover:text-red-400'
                }`}
            >
              <Heart size={24} weight={liked ? 'fill' : 'regular'} />
              <span>{likeCount}</span>
            </button>
          </div>
          <div className="max-w-screen-md mx-auto border-b border-t border-neutral-100 dark:border-neutral-700"></div>
          {renderAuthor()}
          {renderCommentForm()}
          {renderCommentLists()}
        </div>
      </div>
  );
};

export default BlogSingle;